import { Check, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FaFacebook } from "react-icons/fa6";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  OAuthReturnTarget,
  oauthReturnPath,
  parseOAuthReturnTarget,
} from "../lib/oauthReturn";
import { cn } from "../lib/utils";
import { beginWorkspaceOAuth } from "../lib/workspaceApi";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";

// Display data for the Pages a Facebook login granted (no tokens). The OAuth
// callback already connected every new Page ("added") with a pending marker;
// this screen keeps the ticked ones and asks the server to undo the rest.
interface PickerPage {
  id: string;
  name: string;
  picture?: string;
  state: "added" | "connected" | "other_workspace";
  workspaceName: string | null;
}

interface UntickedPage {
  pageName: string;
  inThisWorkspace: boolean;
  workspaceName: string | null;
}

interface PickerData {
  loginId: string;
  /** Epoch ms after which this picker can no longer undo added Pages. */
  expiresAt: number;
  facebookUserName: string;
  pages: PickerPage[];
  unticked: UntickedPage[];
}

const EXPIRED_MESSAGE =
  "Your Facebook Pages are connected. To remove one, disconnect it in Settings.";

const FacebookPageSelector = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, activeWorkspaceId } = useAuth();
  const [data, setData] = useState<PickerData | null>(null);
  const [returnTarget, setReturnTarget] = useState<OAuthReturnTarget>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  useEffect(() => {
    try {
      const parsed = JSON.parse(searchParams.get("data") ?? "");
      setReturnTarget(parseOAuthReturnTarget(parsed));
      if (
        typeof parsed?.loginId !== "string" ||
        typeof parsed?.expiresAt !== "number" ||
        !Array.isArray(parsed?.pages) ||
        !Array.isArray(parsed?.unticked)
      ) {
        throw new Error("malformed");
      }
      // Reopened from history after the window: added Pages are simply
      // connected now, so there is nothing left to choose here.
      if (parsed.expiresAt <= Date.now()) {
        setLoadError(EXPIRED_MESSAGE);
        return;
      }
      const picker = parsed as PickerData;
      setData(picker);
      // Added Pages are already connected; start ticked and let the person
      // untick the ones this workspace shouldn't use.
      setSelected(
        new Set(
          picker.pages.filter((p) => p.state === "added").map((p) => p.id),
        ),
      );
    } catch {
      setLoadError(EXPIRED_MESSAGE);
    }
  }, [searchParams]);

  const added = useMemo(
    () => data?.pages.filter((p) => p.state === "added") ?? [],
    [data],
  );
  const leave = (status?: "success") =>
    navigate(
      oauthReturnPath(returnTarget, status, status ? "facebook" : undefined),
    );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    if (!data || !currentUser) return;
    if (added.length === 0) {
      leave();
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/connections/facebook/confirm-pages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            loginId: data.loginId,
            keepPageIds: Array.from(selected),
          }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || "Couldn't save your Facebook Pages.");
      }
      if (body.expired) {
        setLoadError(EXPIRED_MESSAGE);
        return;
      }
      leave(body.kept > 0 ? "success" : undefined);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't save your Facebook Pages.",
      );
      setIsSaving(false);
    }
  };

  const connectAgain = async () => {
    if (!currentUser || !activeWorkspaceId) return;
    setIsReconnecting(true);
    setError(null);
    try {
      await beginWorkspaceOAuth(
        currentUser,
        activeWorkspaceId,
        "facebook",
        returnTarget,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't open Facebook.");
      setIsReconnecting(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-4">
          <Alert>
            <AlertTitle>This link has expired</AlertTitle>
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
          <Button onClick={() => leave()} variant="outline" className="w-full">
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your Facebook Pages…
        </div>
      </div>
    );
  }

  const unticked = data.unticked;
  const untickedNames = unticked.map((p) => p.pageName);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        <header className="mb-10 text-center">
          <div className="inline-flex items-center justify-center gap-2 mb-3 text-muted-foreground">
            <FaFacebook className="h-5 w-5" />
            <span className="text-xs font-medium uppercase tracking-wider">
              Facebook
            </span>
          </div>
          {/* The big title only when there's a choice to make; otherwise the
              screen is a summary and the one-line intro is enough. */}
          {added.length > 0 && (
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-3">
              Which Pages should this workspace use?
            </h1>
          )}
          <p className="text-base text-muted-foreground">
            {added.length > 0
              ? "Choose the new Pages to connect here."
              : data.pages.length > 0
                ? "These are the Pages Facebook shares with WaveGen."
                : "Facebook is reconnected, but one thing needs your attention."}
          </p>
        </header>

        {unticked.length > 0 && (
          <Alert variant="destructive" className="mb-8">
            <AlertTitle>
              WaveGen can't post to {formatList(untickedNames)} anymore
            </AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                {unticked.length === 1 ? "It wasn't" : "They weren't"} selected
                in Facebook's window just now, so Facebook stopped sharing{" "}
                {unticked.length === 1 ? "it" : "them"}.{" "}
                {describeWhere(unticked)} To fix this, reconnect and choose{" "}
                <strong>Edit settings</strong> in Facebook's window, then select{" "}
                {unticked.length === 1 ? "it" : "them"}.
              </p>
              <Button
                size="sm"
                onClick={connectAgain}
                disabled={isReconnecting || isSaving}
              >
                {isReconnecting && (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                )}
                Reconnect Facebook
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" className="mb-8">
            <AlertTitle>Something didn't go through</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {data.pages.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Pages shared by {data.facebookUserName}
              </p>
              {added.length > 0 && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {selected.size}/{added.length}
                </span>
              )}
            </div>

            <div className="border-t border-border">
              {data.pages.map((page) => (
                <PageRow
                  key={page.id}
                  page={page}
                  selected={selected.has(page.id)}
                  onToggle={() => toggle(page.id)}
                />
              ))}
            </div>

            {/* The unticked-Page warning already carries the same fix. */}
            {unticked.length === 0 && (
              <div className="mt-6 rounded-md border border-border p-4 text-sm">
                <p className="font-medium text-foreground">Missing a Page?</p>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground">
                  <li>
                    <button
                      type="button"
                      onClick={connectAgain}
                      disabled={isReconnecting || isSaving}
                      className="font-medium text-foreground underline underline-offset-2"
                    >
                      Reconnect Facebook
                    </button>
                    .
                  </li>
                  <li>
                    In Facebook's window, choose <strong>Edit settings</strong>,
                    not Continue.
                  </li>
                  <li>Select the Page, and keep your other Pages selected.</li>
                </ol>
                <p className="mt-3 text-xs text-muted-foreground">
                  A Page you leave out stops working in all your WaveGen
                  workspaces.
                </p>
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-end gap-2 mt-8">
          {added.length > 0 ? (
            <Button onClick={save} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {selected.size === 0
                ? "Skip"
                : `Connect ${selected.size} Page${selected.size === 1 ? "" : "s"}`}
            </Button>
          ) : (
            <Button
              // With a warning, Reconnect is the action that matters.
              variant={unticked.length > 0 ? "outline" : "default"}
              onClick={() =>
                leave(
                  data.pages.length === 0 ||
                    data.pages.some((p) => p.state === "connected")
                    ? "success"
                    : undefined,
                )
              }
            >
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

function formatList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function describeWhere(pages: UntickedPage[]): string {
  const places = pages.map((p) =>
    p.inThisWorkspace
      ? "this workspace"
      : p.workspaceName
        ? `your “${p.workspaceName}” workspace`
        : "another workspace",
  );
  const unique = Array.from(new Set(places));
  return `${pages.length === 1 ? "It's" : "They're"} used in ${formatList(unique)}.`;
}

function PageRow({
  page,
  selected,
  onToggle,
}: {
  page: PickerPage;
  selected: boolean;
  onToggle: () => void;
}) {
  const choosable = page.state === "added";
  const detail =
    page.state === "connected"
      ? "Connected to this workspace"
      : page.state === "other_workspace"
        ? page.workspaceName
          ? `Connected in “${page.workspaceName}”`
          : "Connected in another workspace"
        : selected
          ? "Will be connected here"
          : "Won't be connected";
  const checked = choosable && selected;

  return (
    <button
      type="button"
      onClick={choosable ? onToggle : undefined}
      disabled={!choosable}
      aria-pressed={choosable ? selected : undefined}
      className={cn(
        "group w-full flex items-center gap-3 border-t border-border first:border-t-0 py-4 text-left transition-colors",
        choosable
          ? "hover:bg-muted/30 focus-visible:outline-none focus-visible:bg-muted/40"
          : "cursor-default",
      )}
    >
      {page.picture ? (
        <img
          src={page.picture}
          alt=""
          className={cn(
            "h-9 w-9 rounded-full object-cover ring-1 ring-border shrink-0",
            page.state === "other_workspace" && "opacity-50",
          )}
        />
      ) : (
        <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground shrink-0">
          {page.name?.[0]?.toUpperCase() ?? "?"}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-sm font-medium truncate",
            page.state === "other_workspace"
              ? "text-muted-foreground"
              : "text-foreground",
          )}
        >
          {page.name}
        </p>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </div>
      {/* Only Pages this screen decides get a checkbox. A Page already in
          this workspace is removed with Settings' Disconnect, which checks
          scheduled posts first. */}
      {choosable && (
        <span
          className={cn(
            "h-5 w-5 rounded-md border flex items-center justify-center shrink-0 transition-colors",
            checked
              ? "bg-foreground border-foreground text-background"
              : "border-border group-hover:border-foreground/30",
          )}
          aria-hidden
        >
          {checked && <Check className="h-3.5 w-3.5" />}
        </span>
      )}
    </button>
  );
}

export default FacebookPageSelector;
